import { HttpClient } from '@/shared';
import SectionOne from '../../application/domain/entities/SectionOne';
import { ISectionOneRepository } from '../../application/ports/outbound/ISectionOneRepository';
import { SectionOneForm } from '../../application/validators/section-one.schema';
import { SectionOneFormState } from '../../application/domain/models/section-one-form-state';
import { SectionOneResponseDto } from './dto/res/SectionOneResponseDto';
import { SubmitSectionOneResponseDto } from './dto/res/SubmitSectionOneResponseDto';
import { ValidateInvitationResponseDto } from './dto/res/ValidateInvitationResponseDto';
import { SubmitSectionOneResult } from '../../application/domain/entities/SubmitSectionOneResult';
import { InviteValidationResult } from '../../application/domain/entities/InviteValidationResult';
import {
  mapSectionOneDtoToEntity,
  mapSectionOneFormToDto,
  mapStepToStepValue,
  mapStepValueToStep,
} from './mappers/sectionOneMapper';
// import { StepUpdateRequestDto } from './dto/req/stepUpdateRequestDto';
import { StepUpdateResponseDto } from './dto/res/StepUpdateResponseDto';
import { SectionOneSteps } from '../../application/domain/models/section-one-steps';
import { StepUpdateRequestDto } from './dto/req/StepUpdateRequestDto';
import { FormI9StatusResponseDto } from './dto/res/FormI9StatusResponseDto';
import { CountryResponseDto } from './dto/res/CountryResponseDto';
import {
  EmployeeProvidedDocsGetResponseDto,
  EmployeeProvidedDocsPostResponseDto,
} from './dto/res/EmployeeProvidedDocsResponseDto';
import { EmployeeProvidedDocs } from '../../application/domain/entities/EmployeeProvidedDocs';
import { SaveEmployeeProvidedDocsCommand } from '../../application/domain/entities/SaveEmployeeProvidedDocsCommand';
import { EmployeeProvidedDocsRequestDto } from './dto/req/EmployeeProvidedDocsRequestDto';
import {
  mapGetDtoToEntity,
  mapPostDtoToEntity,
  mapCommandToRequestDto,
} from './mappers/employeeProvidedDocsMapper';

export class ApiSectionOneDataRepository implements ISectionOneRepository {
  constructor(private readonly httpClient: HttpClient) {}
  // Mocked fetch - return sample/default values as if from API
  async fetchInitialData(formId: string, isEncrypted?: boolean): Promise<SectionOne | null> {
    const sectionOne = await this.httpClient.get<SectionOneResponseDto>(
      `/i9Form/${formId}/section1`,
      {
        params: {
          isEncrypted,
        },
      }
    );
    return sectionOne.data ? mapSectionOneDtoToEntity(sectionOne.data) : null;
  }

  async createSectionOne(data: SectionOneForm, status: SectionOneFormState): Promise<SectionOne> {
    // Stubbed API call - adapt to your real API client or HttpClient context
    const dto = mapSectionOneFormToDto(data, status);
    const response = await this.httpClient.post<SectionOneResponseDto>('/i9Form/section1', dto);
    return mapSectionOneDtoToEntity(response.data);
  }

  async updateSectionOne(
    data: SectionOneForm,
    status: SectionOneFormState,
    i9FormId: string
  ): Promise<SectionOne> {
    const dto = mapSectionOneFormToDto(data, status);
    const response = await this.httpClient.put<SectionOneResponseDto>('/i9Form/section1', {
      ...dto,
      i9FormId,
    });
    return mapSectionOneDtoToEntity(response.data);
  }

  async markSection1Completed(i9FormId: string): Promise<void> {
    await this.httpClient.patch(`/i9Form/section1/complete`, {
      i9FormId,
    });
  }

  async markSection1Started(i9FormId: string, locationId?: string): Promise<void> {
    await this.httpClient.post('/i9Form/section1/started', {
      i9FormId,
      ...(locationId ? { locationId } : {}),
    });
  }

  async submitSectionOne(
    data: SectionOneForm,
    status: SectionOneFormState,
    i9FormId?: string
  ): Promise<SubmitSectionOneResult> {
    const dto = mapSectionOneFormToDto(data, status);
    const response = await this.httpClient.post<SubmitSectionOneResponseDto>(
      '/i9Form/section1/submit',
      { ...dto, ...(i9FormId ? { i9FormId } : {}) }
    );
    return {
      i9formId: response.data.i9FormId,
      status: response.data.status,
      referenceNumber: response.data.referenceNumber,
      partnerReturnUrl: response.data.partnerReturnUrl,
      routedToReview: response.data.routedToReview === true,
    };
  }

  async resumeAmendment(token: string): Promise<SectionOne | null> {
    const response = await this.httpClient.post<SectionOneResponseDto>(
      '/amendment/section1/resume',
      { token }
    );
    return response.data ? mapSectionOneDtoToEntity(response.data) : null;
  }

  async validateInvite(encryptedI9FormId: string): Promise<InviteValidationResult> {
    const response = await this.httpClient.get<ValidateInvitationResponseDto>(
      `/invitation/validate/${encodeURIComponent(encryptedI9FormId)}`
    );
    return {
      i9formId: response.data.i9FormId,
      status: response.data.status,
      salesForceAccountId: response.data.salesForceAccountId,
      firstName: response.data.firstName,
      lastName: response.data.lastName,
      locationId: response.data.locationId,
      locationLocked: response.data.locationLocked,
      locationName: response.data.locationName ?? null,
      linkClickedAt: response.data.linkClickedAt,
    };
  }

  async updateStep(currentStep: SectionOneSteps, i9FormId: string): Promise<SectionOneSteps> {
    const stepUpdateRequestDto: StepUpdateRequestDto = {
      currentStep: mapStepToStepValue(currentStep),
      i9FormId,
    };
    const response = await this.httpClient.post<StepUpdateResponseDto>(
      `/section1-current-step`,
      stepUpdateRequestDto
    );
    return mapStepValueToStep(response.data.currentStep);
  }

  async fetchFormI9Status(i9FormId: string): Promise<SectionOneFormState> {
    const response = await this.httpClient.get<FormI9StatusResponseDto>(
      `/i9Form/${i9FormId}/status`
    );
    return response.data.status;
  }

  async fetchCountries(): Promise<CountryResponseDto[]> {
    const response = await this.httpClient.get<CountryResponseDto[]>('/country');
    return response.data;
  }

  async fetchEmployeeProvidedDocs(formId: string): Promise<EmployeeProvidedDocs> {
    const response = await this.httpClient.get<EmployeeProvidedDocsGetResponseDto>(
      `/employee-provided-docs/${formId}`
    );
    return mapGetDtoToEntity(response.data);
  }

  async saveEmployeeProvidedDocs(
    command: SaveEmployeeProvidedDocsCommand
  ): Promise<EmployeeProvidedDocs> {
    const dto: EmployeeProvidedDocsRequestDto = mapCommandToRequestDto(command);
    const response = await this.httpClient.post<EmployeeProvidedDocsPostResponseDto>(
      '/employee-provided-docs',
      dto
    );
    return mapPostDtoToEntity(response.data);
  }
}
